import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Container,
  Header,
  Icon,
  Loader,
  Popup,
  Segment,
 } from 'semantic-ui-react';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import config from '../../config';
import { flashErrorMessage, flashSuccessMessage } from '../actions/flash';
import { fetchM } from '../actions/fetchM';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';
import ImportFromProjectModal from './ImportFromProjectModal';
import UploadModal from '../components/UploadModal';
import UploadAttachmentForm from '../containers/BookingPaymentPictures/Form';
import DeleteConfirm from '../containers/modals/DeleteConfirm';
import BusinessUnitDropdownWithCategories from './Sortable/SortablePackagePricing/BusinessUnitDropdownWithCategories';
import Timestamp from './Timestamp';

const FORM_TYPE_CONFIG = {
  token_payment: { label: 'Token Payment', color: '#e67e22' },
  opportunity: { label: 'Opportunity', color: '#16a085' },
  booking: { label: 'Booking', color: '#2185d0' },
};
const DEFAULT_FORM_TYPE = FORM_TYPE_CONFIG.booking;

const DragHandle = SortableHandle(({ idx, disabled }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '5px',
      minWidth: '40px',
      color: '#bbbbbb',
      cursor: disabled ? 'default' : 'grab',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
    }}
  >
    <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
      {idx + 1}
    </span>
    <Icon size="small" disabled={disabled} name="content" />
  </div>
));

const SortableItem = SortableElement(({ data, idx, itemDisabled, renderActionButtons, t }) => (
  <div
    style={{
      border: '1px solid #ddd',
      borderRadius: '5px',
      marginBottom: '10px',
      padding: '10px',
      backgroundColor: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
      <div style={{ marginRight: '10px', minWidth: '50px' }}>
        <DragHandle idx={idx} disabled={itemDisabled} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
          {data.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' }}>
          <Timestamp timestamp={data.createdAt} format="DD/MM/YY" />
        </div>
        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
          {(() => {
            const { label, color } = FORM_TYPE_CONFIG[data.formType] || DEFAULT_FORM_TYPE;
            return (
              <span
                style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  backgroundColor: color,
                  color: '#fff',
                  display: 'inline-block',
                }}
              >
                {t(label, { ns: TRANSLATION_FILES.translation })}
              </span>
            );
          })()}
        </div>
      </div>
    </div>
    <div style={{ marginLeft: '10px' }}>
      {renderActionButtons(data)}
    </div>
  </div>
));

const SortableList = SortableContainer(({ items, onDeleteItem, disabled, renderActionButtons, t }) => (
  <Segment className={items.length > 0 ? 'sortable' : ''} textAlign={items.length > 0 ? null : 'center'}>
    {
      items.length > 0 ? items.map((data, index) => (
        <SortableItem
          data={data}
          idx={index}
          index={index}
          key={`item-${data.id || index}`}
          onDeleteItem={onDeleteItem}
          disabled={disabled}
          itemDisabled={disabled}
          renderActionButtons={renderActionButtons}
          t={t}
        />
      )) : (
        <p>{t('No templates found.', { ns: TRANSLATION_FILES.translation })}</p>
      )
    }
  </Segment>
));

class DocxTemplaterForm extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    projectID: PropTypes.string,
    t: PropTypes.func,
    project: PropTypes.shape({
      printType: PropTypes.string,
      sharedToBankerForms: PropTypes.arrayOf(PropTypes.string),
    }),
    onSubmitBankerForm: PropTypes.func,
  }
  constructor(props) {
    super(props);
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < 768;
    this.state = {
      isMobile,
      loading: true,
      data: [],
      fileUploaded: {},
      showConfirmDelete: false,
      selectedTemplate: null,
      openPopupItemId: null,
      openSharablePopupId: null,
      openFormTypePopupId: null,
      companyBranches: [],
      fullCompanyBranches: [],
    };
  }
  componentDidMount() {
    this.fetchProjectTemplates();
    this.fetchCompanyBranches();
    window.addEventListener('resize', this.handleResize, false);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize, false);
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async fetchProjectTemplates() {
    await this.setLoading(true);
    const { projectID, t } = this.props;
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM(`/docxtemplates/projects/${projectID}/templates/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const token = res.headers.get('X-Csrf-Token');
      const data = await res.json();
      const templatesWithPosition = (data || []).map((template, index) => ({
        ...template,
        position: template.position || index + 1,
      }));
      this.setState({
        data: templatesWithPosition,
        token,
      });
    }
    await this.setLoading(false);
  }
  async uploadTemplate(e, body) {
    e.preventDefault();
    const { projectID, t } = this.props;
    const { store } = this.context;

    if (body instanceof FormData) {
      body.append('form_type', 'booking');
    }

    this.setLoading(true);
    try {
      const res = await store.dispatch(fetchM(`/docxtemplates/projects/${projectID}/templates/`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'X-Csrf-Token': this.state.token,
        },
        body,
      }));

      this.setLoading(false);
      await res.text();
      this.setState({ file: null }, () => {
        store.dispatch(flashSuccessMessage({
          header: t('Upload Success', { ns: TRANSLATION_FILES.translation }),
          message: t('Template successfully uploaded', { ns: TRANSLATION_FILES.translation }),
        }));
      });
      this.fetchProjectTemplates();
      this.handleUploadModalClose();
    } catch (err) {
      this.setLoading(false);

      if (err.res && err.res.status === 409) {
        try {
          const errorData = await err.res.json();
          if (errorData.error === 'duplicate_templates' && errorData.duplicateTemplates) {
            store.dispatch(flashErrorMessage({
              header: t('Error: Identical file name detected', { ns: TRANSLATION_FILES.translation }),
              list: errorData.duplicateTemplates,
            }));
          } else {
            store.dispatch(flashErrorMessage({
              message: errorData.message || t('Failed to upload template', { ns: TRANSLATION_FILES.translation }),
            }));
          }
        } catch (jsonError) {
          err.res.text().then((message) => {
            store.dispatch(flashErrorMessage({ message }));
          });
        }
        this.setState({ file: null });
        return;
      } else if (err.res && err.res.status >= 400 && err.res.status < 500) {
        err.res.text().then((message) => {
          store.dispatch(flashErrorMessage({ message }));
        });
        this.setState({ file: null });
        return;
      }

      store.dispatch(flashErrorMessage(err));
      this.setState({ file: null });
    }
  }
  async fetchTemplate(id) {
    const { store } = this.context;
    const { t } = this.props;
    let res;
    let token;
    try {
      res = await store.dispatch(fetchM(`/docxtemplates/templates/${id}/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return null;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      token = res.headers.get('X-Csrf-Token');
    }
    return token;
  }
  async downloadTemplate(id) {
    await this.setLoading(true);
    const { t } = this.props;
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM(`/docxtemplates/templates/${id}/download/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const data = await res.json();
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
    }
    await this.setLoading(false);
  }
  async previewTemplate(id) {
    await this.setLoading(true);
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(
        fetchM(`/docxtemplates/templates/${id}/preview/`, {
          credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }),
      );
    } catch (e) {
      await this.setState({ loading: false });

      Bugsnag.notifyException(e);
      if (e.res && e.res.status >= 400 && e.res.status < 500) {
        const message = await e.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }

      store.dispatch(flashErrorMessage(e));
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      // Convert the response to a Blob
      const blob = await res.blob();

      // Create a temporary URL for the Blob
      const url = window.URL.createObjectURL(blob);

      // Open the PDF in a new tab for preview
      window.open(url, '_blank');
    }
    await this.setLoading(false);
  }
  async deleteTemplate(id) {
    const { store } = this.context;
    const { t } = this.props;
    const token = await this.fetchTemplate(id);
    this.setLoading(true);
    try {
      await store.dispatch(fetchM(`/docxtemplates/templates/${id}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': token,
        },
      }));
    } catch (err) {
      Bugsnag.notifyException(err);
      if (err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        this.setLoading(false);
        return;
      }
      store.dispatch(flashErrorMessage(err));
    }
    this.setLoading(false);
    this.fetchProjectTemplates();
    store.dispatch(flashSuccessMessage({
      header: t('Delete Success', { ns: TRANSLATION_FILES.translation }),
      message: t('Template successfully deleted', { ns: TRANSLATION_FILES.translation }),
    }));
  }
  async updateTemplate(tmpl) {
    const { store } = this.context;
    const { t } = this.props;
    const token = await this.fetchTemplate(tmpl.id);
    this.setLoading(true);
    try {
      await store.dispatch(fetchM(`/docxtemplates/templates/${tmpl.id}/`, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': token,
        },
        body: JSON.stringify(tmpl),
      }));
    } catch (err) {
      Bugsnag.notifyException(err);
      if (err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        this.setLoading(false);
        return;
      }
      store.dispatch(flashErrorMessage(err));
    }
    this.setLoading(false);
    this.fetchProjectTemplates();
    store.dispatch(flashSuccessMessage({
      header: t('Update Success', { ns: TRANSLATION_FILES.translation }),
      message: t('Template successfully updated', { ns: TRANSLATION_FILES.translation }),
    }));
  }
  async importTemplate(body) {
    this.setLoading(true);
    const { projectID, t } = this.props;
    const { store } = this.context;

    try {
      await store.dispatch(fetchM(`/docxtemplates/projects/${projectID}/templates/import/`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'X-Csrf-Token': this.state.token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }));

      this.setLoading(false);
      this.fetchProjectTemplates();
      store.dispatch(flashSuccessMessage({
        header: t('Import Success', { ns: TRANSLATION_FILES.translation }),
        message: t('Templates successfully imported', { ns: TRANSLATION_FILES.translation }),
      }));
    } catch (err) {
      this.setLoading(false);

      if (err.res && err.res.status === 409) {
        try {
          const errorData = await err.res.json();
          if (errorData.error === 'duplicate_templates' && errorData.duplicateTemplates) {
            store.dispatch(flashErrorMessage({
              header: t('Error: Identical file name detected', { ns: TRANSLATION_FILES.translation }),
              list: errorData.duplicateTemplates,
            }));
          } else {
            store.dispatch(flashErrorMessage({
              message: errorData.message || t('Failed to import templates', { ns: TRANSLATION_FILES.translation }),
            }));
          }
        } catch (jsonError) {
          // If the response isn't valid JSON, fall back to text
          err.res.text().then((message) => {
            store.dispatch(flashErrorMessage({ message }));
          });
        }
        return;
      } else if (err.res && err.res.status >= 400 && err.res.status < 500) {
        err.res.text().then((message) => {
          store.dispatch(flashErrorMessage({ message }));
        });
        return;
      }

      store.dispatch(flashErrorMessage(err));
    }
  }
  async fetchCompanyBranches() {
    const { store } = this.context;
    const { t } = this.props;
    let res;
    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/company_branches/?page=all&getProjects=true`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const branchesData = await res.json();

      // Create dropdown options with categories
      const dropdownOptions = [];

      // Add individual branches with labels
      if (branchesData) {
        branchesData.forEach((ul) => {
          const label = ul.isExternal ? t('Agency', { ns: TRANSLATION_FILES.screens }) : t('Internal', { ns: TRANSLATION_FILES.constants });
          const branch = {
            content: (
              <Header as="h5" style={{ margin: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <div>
                    <div>{ul.shortName}</div>
                    <div style={{ fontSize: '0.8em', color: 'gray' }}>{ul.name}</div>
                  </div>
                </div>
              </Header>
            ),
            description: ul.isExternal ? t('Agency', { ns: TRANSLATION_FILES.screens }) : t('Internal', { ns: TRANSLATION_FILES.constants }),
            key: ul.id,
            name: ul.shortName,
            text: `${ul.shortName} | ${label}`,
            value: ul.id,
          };
          dropdownOptions.push(branch);
        });
      }

      this.setState({
        companyBranches: dropdownOptions,
        fullCompanyBranches: branchesData || [],
      });
    }
  }
  handleUploadModalClose = () => {
    this.uploadModal.close();
  }
  handleCancelDelete = (e) => {
    e.preventDefault();
    this.setState({
      showConfirmDelete: false,
      selectedTemplate: null,
    });
  }
  handleDelete = (e, selectedTemplate) => {
    e.preventDefault();
    this.setState({
      selectedTemplate,
    }, () => {
      this.setState({
        showConfirmDelete: true,
      });
    });
  }
  handleConfirmDelete = (e) => {
    e.preventDefault();
    const { selectedTemplate } = this.state;
    this.setState({
      showConfirmDelete: false,
    }, () => {
      this.deleteTemplate(selectedTemplate.id);
    });
  }
  handleDownload = (e, selectedTemplate) => {
    e.preventDefault();
    this.downloadTemplate(selectedTemplate.id);
  }
  handlePreview = (e, selectedTemplate) => {
    e.preventDefault();
    this.previewTemplate(selectedTemplate.id);
  }
  handleConfirmImport = (body) => {
    this.importTemplate(body);
  }
  handleEnableTemplate = (e, template) => {
    e.preventDefault();
    const newTemp = {
      ...template,
      isEnabled: !template.isEnabled,
    };
    this.updateTemplate(newTemp);
  }
  handleResize = () => {
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < 768;
    const isTablet = windowWidth >= 768 && windowWidth < 992;
    if (isMobile !== this.state.isMobile) {
      this.setState({ isMobile });
    }
    if (isTablet !== this.state.isTablet) {
      this.setState({ isTablet });
    }
  }
  onSortEnd = ({ oldIndex, newIndex }) =>
    this.sortDataByIndex(arrayMove(this.state.data, oldIndex, newIndex));
  getSortedData = data => data.sort((a, b) => (a.position || 0) - (b.position || 0));
  sortDataByIndex = (newData) => {
    const data = newData;
    data.forEach((d, index) => {
      data[index].position = index + 1;
    });
    this.setState({ data }, () => {
      this.updateTemplatePositions();
    });
  }
  updateTemplatePositions = async () => {
    const { store } = this.context;
    const { t } = this.props;

    try {
      const updates = this.state.data.map(template => ({
        id: template.id,
        position: template.position,
      }));
      const updatePromises = updates.map(async (update) => {
        const token = await this.fetchTemplate(update.id);
        return store.dispatch(fetchM(`/docxtemplates/templates/${update.id}/`, {
          credentials: 'include',
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Csrf-Token': token,
          },
          body: JSON.stringify({ position: update.position }),
        }));
      });
      await Promise.all(updatePromises);
    } catch (err) {
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({
        message: t('Failed to update template positions', { ns: TRANSLATION_FILES.translation }),
      }));
    }
  }
  resolveSharedToBanker = () => {
    const { project } = this.props;
    if (!project) {
      return [];
    }
    const sharedToBankerForms = Array.isArray(project.sharedToBankerForms)
      ? project.sharedToBankerForms
      : [];
    return sharedToBankerForms.filter(Boolean);
  }
  resolveTemplateFormType = template => (template && template.formType) || 'booking'
  computeNextBankerFormState = (template) => {
    const { project } = this.props;
    const { data } = this.state;
    const templateID = template.id;
    const formType = this.resolveTemplateFormType(template);
    const currentPrintType = (project && project.printType) || '';
    const currentSharedToBanker = this.resolveSharedToBanker();
    const isSelected = formType === 'booking'
      ? (currentPrintType === templateID || currentSharedToBanker.includes(templateID))
      : currentSharedToBanker.includes(templateID);

    let nextPrintType = currentPrintType;
    let nextSharedToBanker = currentSharedToBanker.filter(id => id !== templateID);

    if (isSelected) {
      if (formType === 'booking' && currentPrintType === templateID) {
        nextPrintType = '';
      }
    } else {
      const sameFormTypeIDs = (data || [])
        .filter(item => this.resolveTemplateFormType(item) === formType)
        .map(item => item.id);

      nextSharedToBanker = nextSharedToBanker.filter(id => !sameFormTypeIDs.includes(id));
      nextSharedToBanker.push(templateID);

      if (formType === 'booking') {
        nextPrintType = templateID;
      }
    }

    if (nextPrintType && !nextSharedToBanker.includes(nextPrintType)) {
      nextSharedToBanker.push(nextPrintType);
    }

    return {
      printType: nextPrintType,
      sharedToBankerForms: Array.from(new Set(nextSharedToBanker)),
    };
  }
  handleUpdateBankerTemplate = (e, template) => {
    e.preventDefault();
    const nextState = this.computeNextBankerFormState(template);
    this.props.onSubmitBankerForm(nextState);
  }
  checkIsBankerFormTemplate = (template) => {
    const { project } = this.props;
    const sharedToBanker = this.resolveSharedToBanker();
    const formType = this.resolveTemplateFormType(template);

    if (formType === 'booking') {
      return project.printType === template.id || sharedToBanker.includes(template.id);
    }
    return sharedToBanker.includes(template.id);
  };
  handleStatusChange = async (itemId, statusType, isChecked) => {
    const template = this.state.data.find(item => item.id === itemId);
    if (!template) return;

    const updatedTemplate = { ...template };

    switch (statusType) {
      case 'reserved':
        updatedTemplate.isEnabledOnReserved = isChecked;
        break;
      case 'booked':
        updatedTemplate.isEnabledOnBooked = isChecked;
        break;
      case 'cancelled':
        updatedTemplate.isEnabledOnCancelled = isChecked;
        break;
      case 'sold':
        updatedTemplate.isEnabledOnSold = isChecked;
        break;
      case 'lawyer':
        updatedTemplate.isVisibleToLawyer = isChecked;
        break;
      case 'srb':
        updatedTemplate.isVisibleToSrb = isChecked;
        break;
      default:
        break;
    }
    await this.updateTemplate(updatedTemplate);
  };
  handleBusinessUnitChange = async (itemId, businessUnitIds) => {
    const template = this.state.data.find(item => item.id === itemId);
    if (!template) return;

    const updatedTemplate = {
      ...template,
      visibleToBusinessUnits: businessUnitIds,
    };
    await this.updateTemplate(updatedTemplate);
  };
  handleFormTypeChange = async (itemId, formType) => {
    const template = this.state.data.find(item => item.id === itemId);
    if (!template) return;
    this.setState({ openFormTypePopupId: null });
    const originalFetchProjectTemplates = this.fetchProjectTemplates;
    this.fetchProjectTemplates = async () => {};
    try {
      if (formType === 'token_payment') {
        const existing = this.state.data.find(templateItem => templateItem.id !== itemId && templateItem.formType === 'token_payment');
        if (existing) {
          await this.updateTemplate({ ...existing, formType: 'booking' });
        }
      }
      await this.updateTemplate({ ...template, formType });
    } finally {
      this.fetchProjectTemplates = originalFetchProjectTemplates;
    }
    await this.fetchProjectTemplates();
  };
  renderActionButtons = (item) => {
    const { t } = this.props;
    const { loading, isMobile } = this.state;
    return (
      <div>
        <div className="ui buttons" style={{ marginRight: '5px' }}>
          <Popup
            content={t('Visible in bookings', { ns: TRANSLATION_FILES.translation })}
            disabled={loading}
            size="mini"
            trigger={
              <Button
                icon
                color={item.isEnabled ? 'blue' : undefined}
                size="small"
                compact
                style={{
                  padding: '8px',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  margin: 0,
                }}
                onClick={e => this.handleEnableTemplate(e, item)}
              >
                <Icon name="eye" color={item.isEnabled ? '' : 'blue'} />
              </Button>
            }
          />
          <Popup
            // content="Set status"
            disabled={loading || !item.isEnabled}
            size="mini"
            flowing
            on="click"
            position={isMobile ? 'bottom left' : 'bottom center'}
            className="status-popup"
            open={this.state.openPopupItemId === item.id}
            onOpen={() => this.setState({ openPopupItemId: item.id })}
            onClose={() => this.setState({ openPopupItemId: null })}
            trigger={
              <Button
                type="button"
                icon
                color={item.isEnabled ? 'blue' : undefined}
                size="small"
                compact
                style={{
                  padding: '8px',
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  margin: 0,
                }}
                disabled={!item.isEnabled}
              >
                <Icon name="dropdown" style={{ margin: 0 }} />
              </Button>
            }
          >
            <div style={{ padding: '5px', minWidth: '300px', maxWidth: '300px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                {t('Visible in booking status', { ns: TRANSLATION_FILES.translation })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div className="ui checkbox" style={{ width: 'auto' }}>
                  <input
                    type="checkbox"
                    id={`reserved-${item.id}`}
                    name="reserved"
                    checked={(item && item.isEnabledOnReserved) || false}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.handleStatusChange(
                        item.id,
                        'reserved',
                        e.target.checked,
                      );
                    }}
                  />
                  <label htmlFor={`reserved-${item.id}`} />
                </div>
                <div style={{ flex: 1, paddingLeft: '8px' }}>
                  <span>{t('Reserved', { ns: TRANSLATION_FILES.constants })}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div className="ui checkbox" style={{ width: 'auto' }}>
                  <input
                    type="checkbox"
                    id={`booked-${item.id}`}
                    name="booked"
                    checked={(item && item.isEnabledOnBooked) || false}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.handleStatusChange(
                        item.id,
                        'booked',
                        e.target.checked,
                      );
                    }}
                  />
                  <label htmlFor={`booked-${item.id}`} />
                </div>
                <div style={{ flex: 1, paddingLeft: '8px' }}>
                  <span>{t('Booked', { ns: TRANSLATION_FILES.constants })}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div className="ui checkbox" style={{ width: 'auto' }}>
                  <input
                    type="checkbox"
                    id={`sold-${item.id}`}
                    name="sold"
                    checked={(item && item.isEnabledOnSold) || false}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.handleStatusChange(item.id, 'sold', e.target.checked);
                    }}
                  />
                  <label htmlFor={`sold-${item.id}`} />
                </div>
                <div style={{ flex: 1, paddingLeft: '8px' }}>
                  <span>{t('Sold', { ns: TRANSLATION_FILES.constants })}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div className="ui checkbox" style={{ width: 'auto' }}>
                  <input
                    type="checkbox"
                    id={`cancelled-${item.id}`}
                    name="cancelled"
                    checked={(item && item.isEnabledOnCancelled) || false}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.handleStatusChange(item.id, 'cancelled', e.target.checked);
                    }}
                  />
                  <label htmlFor={`cancelled-${item.id}`} />
                </div>
                <div style={{ flex: 1, paddingLeft: '8px' }}>
                  <span>Cancelled</span>
                </div>
              </div>

              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e1e1e1' }}>
                <BusinessUnitDropdownWithCategories
                  placeholder={t('Select Business Units', { ns: TRANSLATION_FILES.translation })}
                  value={(item && item.visibleToBusinessUnits) || []}
                  onChange={(businessUnitIds) => {
                    this.handleBusinessUnitChange(item.id, businessUnitIds);
                  }}
                  style={{ maxWidth: '280px' }}
                  companyBranches={this.state.companyBranches}
                  fullCompanyBranches={this.state.fullCompanyBranches}
                />
              </div>
            </div>
          </Popup>
        </div>
        <Popup
          content={t('Shareable to bankers (maximum one form per form type)', { ns: TRANSLATION_FILES.translation })}
          disabled={loading}
          size="mini"
          trigger={
            <Button
              icon
              color={
                this.checkIsBankerFormTemplate(item) ? 'blue' : null
              }
              size="small"
              compact
              style={{ padding: '8px', marginLeft: '5px' }}
              onClick={e => this.handleUpdateBankerTemplate(e, item)}
            >
              <Icon
                name="university"
                color={
                  this.checkIsBankerFormTemplate(item) ? null : 'blue'
                }
              />
            </Button>
          }
        />
        <Popup
          disabled={loading}
          size="mini"
          flowing
          on="click"
          position="bottom center"
          className="status-popup"
          open={this.state.openFormTypePopupId === item.id}
          onOpen={() => this.setState({ openFormTypePopupId: item.id })}
          onClose={() => this.setState({ openFormTypePopupId: null })}
          trigger={
            <Button
              type="button"
              icon
              color="blue"
              size="small"
              compact
              style={{ padding: '8px', marginLeft: '5px' }}
            >
              <Icon
                name="file alternate outline"
                style={{ margin: 0 }}
              />
            </Button>
          }
        >
          <div style={{ padding: '5px', minWidth: '150px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{t('Form Type', { ns: TRANSLATION_FILES.translation })}</div>
            {[
              { value: 'booking', label: 'Booking' },
              { value: 'opportunity', label: 'Opportunity' },
              { value: 'token_payment', label: 'Token Payment' },
            ].map(({ value, label }) => (
              <div
                key={value}
                className="ui checkbox"
                style={{ display: 'block', marginBottom: '8px' }}
              >
                <input
                  type="radio"
                  id={`formtype-${value}-${item.id}`}
                  name={`formtype-${item.id}`}
                  checked={(item.formType || 'booking') === value}
                  onChange={() => this.handleFormTypeChange(item.id, value)}
                />
                <label htmlFor={`formtype-${value}-${item.id}`}>{t(label, { ns: TRANSLATION_FILES.translation })}</label>
              </div>
            ))}
          </div>
        </Popup>
        <Popup
          // content="Share options"
          disabled={loading}
          size="mini"
          flowing
          on="click"
          position="bottom center"
          className="status-popup"
          open={this.state.openSharablePopupId === item.id}
          onOpen={() => this.setState({ openSharablePopupId: item.id })}
          onClose={() => this.setState({ openSharablePopupId: null })}
          trigger={
            <Button
              type="button"
              icon
              color={item.isEnabled ? 'blue' : undefined}
              size="small"
              compact
              style={{ padding: '8px', marginLeft: '5px' }}
              disabled={!item.isEnabled}
            >
              <Icon name="share square" style={{ margin: 0 }} />
            </Button>
          }
        >
          <div style={{ padding: '5px', minWidth: '120px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              {t('Shareable to', { ns: TRANSLATION_FILES.translation })}:
            </div>
            <div
              className="ui checkbox"
              style={{ display: 'block', marginBottom: '8px' }}
            >
              <input
                type="checkbox"
                id={`lawyer-${item.id}`}
                name="lawyer"
                checked={(item && item.isVisibleToLawyer) || false}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.handleStatusChange(item.id, 'lawyer', e.target.checked);
                }}
              />
              <label htmlFor={`lawyer-${item.id}`}>{t('Lawyer', { ns: TRANSLATION_FILES.screens })}</label>
            </div>
            <div
              className="ui checkbox"
              style={{ display: 'block', marginBottom: '8px' }}
            >
              <input
                type="checkbox"
                id={`srb-${item.id}`}
                name="srb"
                checked={(item && item.isVisibleToSrb) || false}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.handleStatusChange(item.id, 'srb', e.target.checked);
                }}
              />
              <label htmlFor={`srb-${item.id}`}>{t('Showroom for Buyer', { ns: TRANSLATION_FILES.translation })}</label>
            </div>
          </div>
        </Popup>
        <Popup
          content={t('Preview', { ns: TRANSLATION_FILES.screens })}
          disabled={loading}
          size="mini"
          trigger={
            <Button
              icon
              color="blue"
              size="small"
              compact
              style={{ padding: '8px', marginLeft: '5px' }}
              onClick={e => this.handlePreview(e, item)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
                style={{ fontSize: '15px', margin: -3 }}
                className="icon icon-tabler icons-tabler-filled icon-tabler-zoom-code"
              >
                <g transform="scale(1.2) translate(-2 -2)">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M14 3.072a8 8 0 0 1 2.32 11.834l5.387 5.387a1 1 0 0 1 -1.414 1.414l-5.388 -5.387a8 8 0 0 1 -12.905 -6.32l.005 -.285a8 8 0 0 1 11.995 -6.643m-5.293 4.22a1 1 0 0 0 -1.414 0l-2 2a1 1 0 0 0 0 1.415l2 2a1 1 0 0 0 1.414 0l.083 -.094a1 1 0 0 0 -.083 -1.32l-1.292 -1.293l1.292 -1.293a1 1 0 0 0 0 -1.414m4 0a1 1 0 0 0 -1.414 0l-.083 .095a1 1 0 0 0 .083 1.32l1.292 1.292l-1.292 1.293a1 1 0 0 0 1.414 1.414l2 -2a1 1 0 0 0 0 -1.414z" />
                </g>
              </svg>
            </Button>
          }
        />
        <Popup
          content={t('Download', { ns: TRANSLATION_FILES.translation })}
          disabled={loading}
          size="mini"
          trigger={
            <Button
              icon
              color="blue"
              size="small"
              compact
              style={{ padding: '8px', marginLeft: '5px' }}
              onClick={e => this.handleDownload(e, item)}
            >
              <Icon name="download" style={{ margin: 0 }} />
            </Button>
          }
        />
        <Popup
          content={t('Delete', { ns: TRANSLATION_FILES.translation })}
          disabled={loading}
          size="mini"
          trigger={
            <Button
              icon
              color="red"
              size="small"
              compact
              style={{ padding: '8px', marginLeft: '5px' }}
              onClick={e => this.handleDelete(e, item)}
              disabled={(item.docxFileKey && item.docxFileKey.indexOf('templates/default_templates/') === 0)}
            >
              <Icon name="trash" style={{ margin: 0 }} />
            </Button>
          }
        />
      </div>
    );
  }

  render() {
    const { data, isMobile, selectedTemplate } = this.state;
    const { projectID, t } = this.props;
    return (
      <Container style={{ maxWidth: '700px' }}>
        <DeleteConfirm
          open={this.state.showConfirmDelete}
          onClose={this.handleCancelDelete}
          onConfirm={this.handleConfirmDelete}
          name={selectedTemplate ? selectedTemplate.name : ''}
        />
        <div style={{ display: 'flex', marginBottom: '12px' }}>
          <div>
            <span style={{ fontWeight: 'bold' }}>&nbsp;&nbsp;{t('Manage Template', { ns: TRANSLATION_FILES.translation })}</span>
            <i>&nbsp;&nbsp;{isMobile ? '(.docx)' : t('(Only applicable to .docx format)', { ns: TRANSLATION_FILES.translation })}</i>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <UploadModal
              trigger={
                <Button
                  style={{ marginLeft: '0.5em' }}
                  primary
                  icon={isMobile}
                  type="button"
                >
                  <Icon name="upload" />
                  {!isMobile && t('Upload File', { ns: TRANSLATION_FILES.translation })}
                </Button>
              }
              ref={(ref) => { this.uploadModal = ref; }}
              header=".docx files"
              loading={this.state.loading}
              onConfirm={(e, file) => {
                this.uploadTemplate(e, file);
              }}
              onClose={this.handleUploadModalClose}
              primary={false}
              content={opt => (
                <UploadAttachmentForm
                  fileType="docx"
                  btnText={t('Files', { ns: TRANSLATION_FILES.translation })}
                  loading={this.state.loading}
                  onSubmit={opt.onConfirm}
                />
              )}
            />
            <ImportFromProjectModal
              projectID={projectID}
              onConfirm={this.handleConfirmImport}
              trigger={
                <Button
                  style={{ marginLeft: '5px' }}
                  primary
                  icon={isMobile}
                  type="button"
                >
                  <Icon name="folder open" />
                  {!isMobile && t('Import from project', { ns: TRANSLATION_FILES.translation })}
                </Button>
              }
            />
          </div>
        </div>
        <div style={{ marginTop: '20px' }}>
          <SortableList
            helperClass="docxTemplates isSorting"
            items={this.getSortedData(data)}
            lockAxis="y"
            lockToContainerEdges
            onSortEnd={this.onSortEnd}
            useDragHandle
            disabled={this.state.loading}
            renderActionButtons={this.renderActionButtons}
            t={t}
          />
          {this.state.loading && (
            <Segment textAlign="center">
              <Loader active inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
            </Segment>
          )}
        </div>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(DocxTemplaterForm);



// WEBPACK FOOTER //
// ./src/components/DocxTemplaterForm.jsx